import React, { useState } from 'react'
import log from 'loglevel'
import {
  useHideModal,
  useIsShowModal,
  useSetActiveModal,
} from 'src/Modal/ModalStateContext'
import { ModalForm, ModalFormId } from '../..'
import { Spinner } from '../../../../Throbber'
import { useContent } from '../../../../context/hooks'
import { useFrameworkWizard } from '../../../../context/FormContext'
import { filterOverviewsByComplianceFrameworkIds } from '../complianceFrameworkUtils'
import {
  useComplianceFrameworks,
  useEnableComplianceFramework,
} from '../../../async/complianceframework/useComplianceFrameworks'
import { showInfoToast } from '../../../../Utils/helpers/toast'
import { useAnalytics } from '../../../../analytics/useAnalytics'
import {
  ConfirmFrameworkCongrats,
  ConfirmFrameworkSpinnerContainer,
} from './ConfirmFrameworkModal.styles'

export const ConfirmFrameworkModal = (): JSX.Element => {
  const { overviews } = useContent()
  const {
    controlsState: { selectedControls, controlsInAccount },
    policiesState: { selectedPolicies, policiesInAccount },
    frameworkState: { framework },
  } = useFrameworkWizard()
  const { track, events } = useAnalytics()
  const enableComplianceFramework = useEnableComplianceFramework()
  const show = useIsShowModal(ModalFormId.CONFIRM_FRAMEWORK)
  const hide = useHideModal({ modalId: ModalFormId.CONFIRM_FRAMEWORK })
  const back = useSetActiveModal(ModalFormId.SELECT_POLICIES)
  // create a separate loading state for enabling the compliance framework
  // this way, we can block exiting the modal only if this loading state is true
  const [isFrameworkLoading, setIsFrameworkLoading] = useState<boolean>(false)
  const blockHideIfFrameworkLoading = () => {
    !isFrameworkLoading && hide()
  }
  const enabledFrameworks = useComplianceFrameworks(true)
  const enabledFrameworkIds = enabledFrameworks.data
    ? enabledFrameworks.data
        .getItemsList()
        .map((framework) => framework.getModelId())
    : []
  const templateOverviewNames = framework
    ? filterOverviewsByComplianceFrameworkIds(overviews, [
        framework?.getModelId(),
      ]).map((overview) => overview.name)
    : []
  const currentOverviewNames = framework
    ? filterOverviewsByComplianceFrameworkIds(
        overviews,
        enabledFrameworkIds,
      ).map((overview) => overview.name)
    : []
  const newOverviews = templateOverviewNames.filter(
    (overviewName) => !currentOverviewNames.includes(overviewName),
  )

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault()
      if (!framework) {
        return
      }
      setIsFrameworkLoading(true)
      track(events.CREATE_FRAMEWORK, {
        modelId: framework.getModelId(),
        name: framework.getName(),
      })
      await enableComplianceFramework()
    } catch (e) {
      log.error('error enabling compliance framework:', e)
      showInfoToast('Framework setup failed.')
    } finally {
      setIsFrameworkLoading(false)
      hide()
    }
  }

  return (
    <ModalForm
      show={show}
      hide={blockHideIfFrameworkLoading}
      formId={ModalFormId.CONFIRM_FRAMEWORK}
      size="xl"
      title={`You're Ready to Get Started`}
      submitText="Finish Setup"
      onBack={back}
      hideButtons={isFrameworkLoading}
    >
      {isFrameworkLoading ? (
        <ConfirmFrameworkSpinnerContainer>
          <Spinner color={'primary'} size={'xl'} />
          Finalizing changes. This may take up to a minute.
        </ConfirmFrameworkSpinnerContainer>
      ) : (
        <form id={ModalFormId.CONFIRM_FRAMEWORK} onSubmit={handleSubmit}>
          <p>This will</p>
          <ul>
            <li>{`add ${selectedControls.length} controls`}</li>
            <li>{`add ${selectedPolicies.length} policies`}</li>
            <li>{`add ${newOverviews.length} company info items`}</li>
            {!!controlsInAccount.length && (
              <li>{`link ${controlsInAccount.length} controls to ${
                framework?.getName() || ''
              }`}</li>
            )}
            {!!policiesInAccount.length && (
              <li>{`link ${policiesInAccount.length} policies to ${
                framework?.getName() || ''
              }`}</li>
            )}
          </ul>
          <ConfirmFrameworkCongrats />
        </form>
      )}
    </ModalForm>
  )
}
